import { getLocale, t } from '@i18n';
import H from '@utils/helpers';
import { formatMoney, getPlanValue } from '@utils/money';

export function buildSummaryPrice(plan: any, selectedInstallments: any, coupon: any) {
  const currency = String(plan?.currency || 'BRL').toUpperCase();
  const supportsInstallments = currency === 'BRL';
  const installments = supportsInstallments
    ? Math.max(Number(selectedInstallments || plan?.installments || 1), 1)
    : 1;
  const showInstallments = supportsInstallments && installments > 1;
  const baseAmount = Math.round(getPlanValue(plan) * 100);
  const installmentTotal = supportsInstallments && Array.isArray(plan?.installments_list)
    ? Number(plan.installments_list[installments - 1] || baseAmount)
    : baseAmount;
  const discountPercent = Number(coupon?.percent_off || 0);
  const total = Math.max(Math.round(installmentTotal * (1 - (discountPercent / 100))), 0);
  const installmentValue = showInstallments ? Math.round(total / installments) : total;

  return {
    price: formatMoney(installmentValue, currency),
    total: formatMoney(total, currency),
    installments,
    showInstallments,
    couponLabel: discountPercent > 0
      ? `${coupon?.code || t('common.discountCoupon')} - ${discountPercent}% OFF`
      : null,
    isLifetime: plan?.interval === 'lifetime',
  };
}

export function buildRenewalMessage(plan: any) {
  if (!isAutoRenewActive(plan)) {
    return null;
  }

  const renewalDate = getPlanRenewalDate(plan);

  return t('checkout.summary.renewal', { date: formatRenewalDate(renewalDate) });
}

function isAutoRenewActive(plan: any) {
  const explicitValue = [
    plan?.autorenew,
    plan?.autoRenew,
    plan?.auto_renew,
    plan?.autoRenewal,
    plan?.auto_renewal,
    plan?.recurring,
    plan?.isRecurring,
    plan?.renews,
  ].find((value) => value !== undefined && value !== null);

  if (explicitValue !== undefined) {
    if (typeof explicitValue === 'boolean') return explicitValue;
    if (typeof explicitValue === 'number') return explicitValue > 0;
    if (typeof explicitValue === 'object') return true;

    return ['true', '1', 'active', 'enabled', 'yes', 'recurring', 'subscription'].includes(
      String(explicitValue).toLowerCase()
    );
  }

  const interval = String(plan?.interval || '').toLowerCase();

  return !!interval && !['lifetime', 'once', 'one_time', 'one-time', 'single'].includes(interval);
}

function getPlanRenewalDate(plan: any) {
  const rawDate = [
    plan?.renewalDate,
    plan?.renewal_date,
    plan?.renewsAt,
    plan?.renews_at,
    plan?.renewAt,
    plan?.renew_at,
    plan?.nextRenewalDate,
    plan?.next_renewal_date,
    plan?.currentPeriodEnd,
    plan?.current_period_end,
  ].find(Boolean);
  const parsedDate = parsePlanDate(rawDate);

  if (parsedDate) {
    return parsedDate;
  }

  return estimateRenewalDate(plan);
}

function parsePlanDate(value: any) {
  if (!value) {
    return null;
  }

  const timestamp = typeof value === 'number'
    ? (value < 1000000000000 ? value * 1000 : value)
    : Date.parse(value);
  const date = new Date(timestamp);

  return Number.isNaN(date.getTime()) ? null : date;
}

function estimateRenewalDate(plan: any) {
  const interval = String(plan?.interval || '').toLowerCase();
  const intervalCount = Math.max(Number(plan?.intervalCount || plan?.interval_count || 1), 1);
  const date = new Date();

  if (interval.includes('year') || interval.includes('annual') || interval.includes('ano')) {
    date.setFullYear(date.getFullYear() + intervalCount);
  } else if (interval.includes('semester') || interval.includes('semi')) {
    date.setMonth(date.getMonth() + (intervalCount * 6));
  } else if (interval.includes('quarter') || interval.includes('trimestre')) {
    date.setMonth(date.getMonth() + (intervalCount * 3));
  } else if (interval.includes('week') || interval.includes('semana')) {
    date.setDate(date.getDate() + (intervalCount * 7));
  } else if (interval.includes('day') || interval.includes('dia')) {
    date.setDate(date.getDate() + intervalCount);
  } else {
    date.setMonth(date.getMonth() + intervalCount);
  }

  return date;
}

function formatRenewalDate(date: Date) {
  return new Intl.DateTimeFormat(getLocale(), {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

export function getPlanIntervalLabel(plan: any) {
  if (plan?.interval === 'lifetime') {
    return t('interval.lifetime.access');
  }

  if (!plan?.interval) {
    return t('interval.period');
  }

  return H.Parser.interval.noun(plan?.interval).toLowerCase();
}
